import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFirestore, initializeFirestore } from 'firebase/firestore';
import { getDatabase, connectDatabaseEmulator } from 'firebase/database';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: process.env.VITE_FIREBASE_API_KEY,
  authDomain: 'ltpautomatedpublisherscorecard.firebaseapp.com',
  projectId: 'ltpautomatedpublisherscorecard',
  storageBucket: 'ltpautomatedpublisherscorecard.appspot.com',
  messagingSenderId: '733755540125',
  appId: '1:733755540125:web:78050227021d6004330a74',
  // databaseURL is optional - only needed if Realtime Database is enabled
  // databaseURL: process.env.VITE_FIREBASE_DATABASE_URL || 'https://ltpautomatedpublisherscorecard-default-rtdb.firebaseio.com',
};

// Initialize Firebase app only if it hasn't been initialized
const existingApps = getApps();
export const app = existingApps.length === 0 ? initializeApp(firebaseConfig) : (existingApps[0] ?? initializeApp(firebaseConfig));

// Initialize auth - getAuth registers the auth component automatically
// Ensure app is fully initialized before calling getAuth
export const authFirebase = getAuth(app);

// Secondary auth instance for admin creating new users - keeps main session unchanged
const ADMIN_CREATE_APP_NAME = 'AdminCreateUser';
const adminCreateApp =
  getApps().find((a) => a.name === ADMIN_CREATE_APP_NAME)
    ? getApp(ADMIN_CREATE_APP_NAME)
    : initializeApp(firebaseConfig, ADMIN_CREATE_APP_NAME);
export const authFirebaseAdminCreate = getAuth(adminCreateApp);
// export const storage = getStorage(app);
export const db =
  process.env.VITE_ENVIRONMENT === 'development'
    ? initializeFirestore(app, {}, 'dev-env')
    : process.env.VITE_ENVIRONMENT === 'staging'
      ? initializeFirestore(app, {}, 'stg-env')
      : getFirestore(app);

// Initialize Analytics with error handling
// Analytics may not be available in all environments (e.g., localhost, non-browser)
let analyticsInstance: Analytics | null = null;

/**
 * Get Firebase Analytics instance
 * Returns null if Analytics is not supported or not available
 * This is safe to call - it will not throw an error if Analytics is unavailable
 */
export const getFirebaseAnalytics = async (): Promise<Analytics | null> => {
  if (analyticsInstance !== null) {
    return analyticsInstance;
  }

  try {
    // Check if Analytics is supported in this environment
    const supported = await isSupported();
    if (!supported) {
      console.warn('Firebase Analytics is not supported in this environment');
      return null;
    }

    // Initialize Analytics
    analyticsInstance = getAnalytics(app);
    return analyticsInstance;
  } catch (error: any) {
    // Analytics initialization failed (e.g., not in browser, or not configured)
    const errorMessage = error?.message || String(error);
    console.warn('Firebase Analytics initialization failed:', errorMessage);
    return null;
  }
};

// Initialize Realtime Database with error handling
// Realtime Database may not be enabled in all Firebase projects
// We use a function to lazy-load the database to avoid errors at module load time
let rtdbInstance: ReturnType<typeof getDatabase> | null = null;

/**
 * Get Firebase Realtime Database instance
 * Returns null if Realtime Database is not enabled in the Firebase project
 * This is safe to call - it will not throw an error if the service is unavailable
 */
export const getRealtimeDatabase = (): ReturnType<typeof getDatabase> | null => {
  if (rtdbInstance !== null) {
    return rtdbInstance;
  }

  try {
    // Try to initialize Realtime Database
    // This will fail if Realtime Database is not enabled in the Firebase project
    rtdbInstance = getDatabase(app);
    return rtdbInstance;
  } catch (error: any) {
    // Realtime Database is not available or not configured
    // This is expected if the project doesn't use Realtime Database
    const errorMessage = error?.message || String(error);
    if (errorMessage.includes('not available') || errorMessage.includes('not-available')) {
      // Silently handle - this is expected if Realtime Database is not enabled
      return null;
    }
    console.warn('Firebase Realtime Database initialization failed:', errorMessage);
    return null;
  }
};

// Export rtdb as a lazy proxy for backward compatibility
// This defers initialization until the database is actually accessed
// If the database is not available, property access will return undefined
export const rtdb = new Proxy({} as ReturnType<typeof getDatabase>, {
  get(_target, prop) {
    const db = getRealtimeDatabase();
    if (!db) {
      // Return undefined for any property access if DB is not available
      return undefined;
    }
    return (db as any)[prop];
  },
  has(_target, prop) {
    const db = getRealtimeDatabase();
    return db ? prop in db : false;
  }
});